/**
 * Authenticated fetch utility that automatically adds Stytch JWT to requests
 */
/**
 * Helper function to get cookie value
 */
function getCookieValue(name) {
    if (typeof window === 'undefined')
        return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        const cookieValue = parts.pop()?.split(';').shift();
        return cookieValue ? decodeURIComponent(cookieValue) : null;
    }
    return null;
}
/**
 * Get Stytch JWT from cookies (primary method)
 */
/**
 * Get Stytch session JWT from cookies
 */
function getAuthToken() {
    try {
        // Read JWT directly from cookies
        const jwt = getCookieValue('stytch_session_jwt');
        return jwt;
    }
    catch (error) {
        console.error('❌ [AuthFetch] Error getting session JWT from cookies:', error);
        return null;
    }
}
/**
 * Fetch wrapper that automatically includes Stytch authentication
 */
export async function authenticatedFetch(url, options = {}) {
    // Reduced logging for better performance
    // Only log errors and critical information
    try {
        const token = getAuthToken();
        // Prepare headers - don't set Content-Type for FormData (browser will set it automatically with boundary)
        const headers = {
            ...options.headers,
        };
        // Only set Content-Type to application/json if body is not FormData
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }
        // If we have a JWT token, add it to Authorization header
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        // Special handling for onboarding routes - they handle Stytch tokens directly via request body
        if (url.includes('/api/onboarding/')) {
            console.log('🎯 [AuthFetch] Onboarding route detected, proceeding without JWT (API will handle Stytch tokens)');
            // Remove Authorization header for onboarding routes
            delete headers['Authorization'];
        }
        // Make the request with auth headers and cookies
        return fetch(url, {
            ...options,
            headers,
            credentials: 'include', // Always include cookies for server-side auth
        });
    }
    catch (error) {
        console.error('❌ [AuthFetch] Error making authenticated request to:', url, error);
        throw error;
    }
}
/**
 * Convenience methods for common HTTP verbs
 */
export const authFetch = {
    get: (url, options = {}) => authenticatedFetch(url, { ...options, method: 'GET' }),
    post: (url, data, options = {}) => authenticatedFetch(url, {
        ...options,
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
    }),
    put: (url, data, options = {}) => authenticatedFetch(url, {
        ...options,
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
    }),
    delete: (url, options = {}) => authenticatedFetch(url, { ...options, method: 'DELETE' }),
    patch: (url, data, options = {}) => authenticatedFetch(url, {
        ...options,
        method: 'PATCH',
        body: data ? JSON.stringify(data) : undefined,
    }),
};
